"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  GRE_ROWS,
  GRE_WRITING_ROWS,
  type GreMeasure,
  grePercentile,
  greTotal,
  isGreScore,
  lowestScoreForPercentile,
} from "@/lib/tool-kit/exam-scores/gre";

/** English strings. A translated content file overrides these through `ui`. */
export const GRE_PERCENTILE_CALCULATOR_STRINGS = {
  inputHeading: "Your GRE scores",
  inputHint: "Fill in any of the three. Leave the others blank.",
  measures: {
    verbal: "Verbal Reasoning",
    quant: "Quantitative Reasoning",
    writing: "Analytical Writing",
  },
  scales: {
    verbal: "130–170",
    quant: "130–170",
    writing: "0–6, in half points",
  },
  scoreLabel: "{measure} ({scale})",
  invalid: "{measure}: that is not a possible score on the {scale} scale.",
  results: "Percentile ranks",
  percentileRank: "{measure} percentile rank",
  scoreLine: "{score} → {percentile}",
  below: "{percentile}% of test takers scored lower than {score}.",
  atOrAbove: "About {percentile}% scored {score} or higher.",
  notReported:
    "ETS reports no percentile rank for {score}: nobody scored below it in the reference group.",
  totalNote:
    "The two reasoning scores add to {total} out of 340. ETS publishes no percentile rank for the total, so compare each measure on its own.",
  enterScore: "Enter at least one GRE score to see its percentile rank.",
  targetHeading: "Work backward from a percentile",
  targetLabel: "Percentile you want (1–99)",
  targetResult: "{measure}: {score} or higher",
  targetNone: "{measure}: no score reaches that percentile",
  targetInvalid: "Enter a whole number from 1 to 99.",
  referenceNote:
    "ETS percentile ranks, based on everyone who tested between {start} and {end}. A percentile rank is the percent of test takers who scored lower than the score.",
  referenceStart: "July 1, 2022",
  referenceEnd: "June 30, 2025",
  tableHeading: "ETS percentile table",
  colScore: "Score",
  colVerbal: "Verbal Reasoning",
  colQuant: "Quantitative Reasoning",
  colWriting: "Analytical Writing",
  writingHeading: "Analytical Writing",
  dash: "—",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "GRE {measure} {score}: {percentile} percentile rank",
} as const;

type Strings = typeof GRE_PERCENTILE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

const MEASURES: readonly GreMeasure[] = ["verbal", "quant", "writing"];
const WRITING_FORMAT = { minimumFractionDigits: 1, maximumFractionDigits: 1 };

function scoreText(measure: GreMeasure, value: number, fmt: Format): string {
  return measure === "writing" ? fmt(value, WRITING_FORMAT) : fmt(value);
}

type Entries = Record<GreMeasure, string>;

function readScore(raw: string): number | null {
  return raw.trim() === "" ? null : parseNumberInput(raw);
}

function MeasureResult({
  measure,
  score,
  strings,
  fmt,
}: {
  measure: GreMeasure;
  score: number;
  strings: Strings;
  fmt: Format;
}) {
  const result = grePercentile(measure, score);
  const label = strings.measures[measure];
  const shown = scoreText(measure, score, fmt);
  if (result === null) {
    return null;
  }
  if (result.below === null) {
    return (
      <div className="space-y-1">
        <StatCard label={label} value={shown} />
        <p className="text-muted-foreground text-sm">
          {t(strings.notReported, { score: shown })}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <StatCard
        emphasis
        label={t(strings.percentileRank, { measure: label })}
        value={t(strings.scoreLine, {
          score: shown,
          percentile: fmt(result.below),
        })}
      />
      <p className="text-muted-foreground text-sm">
        {t(strings.below, { percentile: fmt(result.below), score: shown })}{" "}
        {t(strings.atOrAbove, {
          percentile: fmt(100 - result.below),
          score: shown,
        })}
      </p>
    </div>
  );
}

function TargetPanel({ strings, fmt }: { strings: Strings; fmt: Format }) {
  const [raw, setRaw] = useState("90");
  const value = readScore(raw);
  const valid =
    value !== null && Number.isInteger(value) && value >= 1 && value <= 99;
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.targetHeading}</h3>
      <TextField
        id="gre-target"
        label={strings.targetLabel}
        numeric
        onChange={setRaw}
        value={raw}
      />
      <div aria-live="polite" className="space-y-1 text-foreground text-sm">
        {valid
          ? MEASURES.map((measure) => {
              const score = lowestScoreForPercentile(measure, value);
              const measureName = strings.measures[measure];
              return (
                <p key={measure}>
                  {score === null
                    ? t(strings.targetNone, { measure: measureName })
                    : t(strings.targetResult, {
                        measure: measureName,
                        score: scoreText(measure, score, fmt),
                      })}
                </p>
              );
            })
          : null}
        {valid || raw.trim() === "" ? null : (
          <Notice>{strings.targetInvalid}</Notice>
        )}
      </div>
    </section>
  );
}

export default function GrePercentileCalculatorWidget() {
  const strings = useToolUi(GRE_PERCENTILE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [entries, setEntries] = useState<Entries>({
    verbal: "160",
    quant: "165",
    writing: "4",
  });
  const scores = MEASURES.map((measure) => ({
    measure,
    raw: entries[measure],
    value: readScore(entries[measure]),
  }));
  const valid = scores.flatMap((entry) =>
    isGreScore(entry.measure, entry.value)
      ? [{ measure: entry.measure, score: entry.value }]
      : []
  );
  const verbal = valid.find((entry) => entry.measure === "verbal");
  const quant = valid.find((entry) => entry.measure === "quant");
  const copyLines = valid.flatMap((entry) => {
    const result = grePercentile(entry.measure, entry.score);
    return result?.below == null
      ? []
      : [
          t(strings.summary, {
            measure: strings.measures[entry.measure],
            score: scoreText(entry.measure, entry.score, fmt),
            percentile: fmt(result.below),
          }),
        ];
  });

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.inputHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.inputHint}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {MEASURES.map((measure) => (
          <TextField
            id={`gre-${measure}`}
            key={measure}
            label={t(strings.scoreLabel, {
              measure: strings.measures[measure],
              scale: strings.scales[measure],
            })}
            numeric
            onChange={(value) =>
              setEntries((all) => ({ ...all, [measure]: value }))
            }
            value={entries[measure]}
          />
        ))}
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {scores.map((entry) =>
          entry.raw.trim() !== "" && !isGreScore(entry.measure, entry.value) ? (
            <Notice key={entry.measure}>
              {t(strings.invalid, {
                measure: strings.measures[entry.measure],
                scale: strings.scales[entry.measure],
              })}
            </Notice>
          ) : null
        )}
        {valid.length === 0 ? <Notice>{strings.enterScore}</Notice> : null}
        {valid.length > 0 ? (
          <StatGrid>
            {valid.map((entry) => (
              <MeasureResult
                fmt={fmt}
                key={entry.measure}
                measure={entry.measure}
                score={entry.score}
                strings={strings}
              />
            ))}
          </StatGrid>
        ) : null}
        {verbal && quant ? (
          <p className="text-muted-foreground text-sm">
            {t(strings.totalNote, {
              total: fmt(greTotal(verbal.score, quant.score)),
            })}
          </p>
        ) : null}
        {copyLines.length > 0 ? (
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={copyLines.join("\n")}
          />
        ) : null}
        <p className="text-muted-foreground text-sm">
          {t(strings.referenceNote, {
            start: strings.referenceStart,
            end: strings.referenceEnd,
          })}
        </p>
      </section>

      <TargetPanel fmt={fmt} strings={strings} />

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable
          head={[strings.colScore, strings.colVerbal, strings.colQuant]}
          rows={GRE_ROWS.map((row) => [
            fmt(row.score),
            row.verbal === null ? strings.dash : fmt(row.verbal),
            row.quant === null ? strings.dash : fmt(row.quant),
          ])}
        />
        <DataTable
          head={[strings.colScore, strings.colWriting]}
          rows={GRE_WRITING_ROWS.map((row) => [
            fmt(row.score, WRITING_FORMAT),
            row.percentile === null ? strings.dash : fmt(row.percentile),
          ])}
        />
      </section>
    </div>
  );
}
